import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import axios from "axios";
import bg3 from "../images/blogs/Bg/bg-3.png";
import dots from "../images/blogs/dots.svg";
import yellow from "../images/blogs/yellow.svg";
import { api_url } from "../config/config";

const BlogDetails = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [allBlogs, setAllBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchBlog = async () => {
      try {
        const res = await axios.get(`${api_url}/blog`);
        const blogData = Array.isArray(res.data?.data) ? res.data.data : [];
        const found = blogData.find((b) => b.slug === slug);
        if (found) {
          setBlog(found);
          // Recent blogs: exclude current, max 5
          setAllBlogs(blogData.filter((b) => b.slug !== slug).slice(0, 5));
        } else {
          setError("Blog not found.");
        }
      } catch {
        setError("Failed to fetch blog details.");
      } finally {
        setLoading(false);
      }
    };
    fetchBlog();
  }, [slug]);

  const formatDate = (d) =>
    new Date(d).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  const formatDateLong = (d) =>
    new Date(d).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });

  /* ── Loading ── */
  if (loading)
    return (
      <div className="min-h-screen bg-[#f8fafa] font-poppins">
        <div className="px-5 sm:px-10 lg:px-16 py-6">
          <div className="h-4 w-48 bg-[#e2eaea] rounded-full animate-pulse mb-8" />
          <div className="flex gap-8">
            <div className="flex-1" style={{ flex: "0 0 75%" }}>
              <div className="w-full h-[420px] bg-[#e2eaea] rounded-2xl animate-pulse mb-6" />
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className={`h-4 bg-[#e2eaea] rounded-full animate-pulse mb-3 ${i % 3 === 0 ? "w-2/3" : "w-full"}`}
                />
              ))}
            </div>
            <div style={{ flex: "0 0 25%" }} className="flex flex-col gap-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  className="bg-white rounded-xl border border-[#e2eaea] p-3 animate-pulse flex gap-3"
                >
                  <div className="w-16 h-16 bg-[#e2eaea] rounded-lg flex-shrink-0" />
                  <div className="flex-1 flex flex-col gap-2 justify-center">
                    <div className="h-3 bg-[#e2eaea] rounded-full w-full" />
                    <div className="h-3 bg-[#e2eaea] rounded-full w-3/4" />
                    <div className="h-2 bg-[#e2eaea] rounded-full w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );

  /* ── Error ── */
  if (error)
    return (
      <div className="min-h-screen bg-[#f8fafa] font-poppins flex flex-col items-center justify-center gap-4 px-5">
        <div className="w-16 h-16 rounded-2xl bg-[#feecec] border border-[#f5b8b8] flex items-center justify-center">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#c0392b"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <p className="text-[15px] font-semibold text-[#374747]">{error}</p>
        <Link
          to="/blogs"
          className="text-[13px] font-semibold text-[#1e4a46] hover:text-[#ff601e] transition-colors flex items-center gap-1"
        >
          ← Back to blogs
        </Link>
      </div>
    );

  const bannerImage = blog.blog_images?.[0]?.image_url || bg3;
  const secondImage = blog.blog_images?.[1]?.image_url || null;
  const readTime = blog.content
    ? Math.max(1, Math.ceil(blog.content.length / 1000))
    : 1;

  return (
    <>
      <Helmet>
        <title>{blog.title} | MediSYS Blog</title>
        <meta name="description" content={blog.subtitle || blog.title} />
      </Helmet>

      <div className="min-h-screen bg-[#f8fafa] font-poppins">
        {/* ══════════════════════════════════════════════
            BREADCRUMB — top, full width
        ══════════════════════════════════════════════ */}
        <div className="bg-white border-b border-[#e2eaea] px-5 sm:px-10 lg:px-16 py-3.5">
          <nav className="flex items-center gap-2 text-[12px] font-medium flex-wrap">
            <Link
              to="/home"
              className="text-[#8faba9] hover:text-[#1e4a46] transition-colors"
            >
              Home
            </Link>
            <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
              <path
                d="M6 12l4-4-4-4"
                stroke="#c5d5d3"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <Link
              to="/blogs"
              className="text-[#8faba9] hover:text-[#1e4a46] transition-colors"
            >
              Blog
            </Link>
            <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
              <path
                d="M6 12l4-4-4-4"
                stroke="#c5d5d3"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {blog.blog_type?.name && (
              <>
                <Link
                  to="/blogs"
                  className="text-[#8faba9] hover:text-[#1e4a46] transition-colors"
                >
                  {blog.blog_type.name}
                </Link>
                <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M6 12l4-4-4-4"
                    stroke="#c5d5d3"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </>
            )}
            <span className="text-[#1e4a46] font-semibold truncate max-w-[280px]">
              {blog.title}
            </span>
          </nav>
        </div>

        {/* ══════════════════════════════════════════════
            MAIN LAYOUT — 75% content + 25% sidebar
        ══════════════════════════════════════════════ */}
        <div className="px-5 sm:px-10 lg:px-16 py-8 sm:mt-0 mt-24">
          <div className="flex flex-col lg:flex-row gap-8 items-start">
            {/* ── LEFT: 75% — Image + Article Content ── */}
            <div className="w-full lg:w-[75%] min-w-0">
              {/* Hero Image */}
              <div
                className="relative w-full rounded-2xl overflow-hidden shadow-[0_8px_32px_rgba(22,44,42,0.14)] border border-[#e2eaea] mb-7"
                style={{ height: "420px" }}
              >
                <img
                  src={bannerImage}
                  alt={blog.title}
                  className="w-full h-full object-cover object-center block"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = bg3;
                  }}
                />
                {/* Subtle bottom gradient for badge legibility */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a1715]/70 via-transparent to-transparent" />

                {/* Category badge */}
                {blog.blog_type?.name && (
                  <span className="absolute top-5 left-5 bg-[#ff601e] text-white text-[10px] font-bold px-3 py-1.5 rounded-full uppercase tracking-wide shadow-[0_2px_10px_rgba(255,96,30,0.5)]">
                    {blog.blog_type.name}
                  </span>
                )}

                {/* Meta row overlaid at bottom of image */}
                <div className="absolute bottom-0 left-0 right-0 px-6 pb-5 pt-10 bg-gradient-to-t from-[#0a1715]/90 to-transparent">
                  <h1 className="font-bold text-white text-[20px] sm:text-[26px] lg:text-[30px] leading-tight mb-3 max-w-[700px]">
                    {blog.title}
                  </h1>
                  <div className="flex flex-wrap items-center gap-3 sm:gap-5">
                    {/* Author */}
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-[#1e4a46] border-2 border-[#5fbdb3]/40 flex items-center justify-center flex-shrink-0">
                        <svg
                          width="11"
                          height="11"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="white"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                      </div>
                      <span className="text-[12px] font-semibold text-white/90">
                        {blog.author}
                      </span>
                    </div>
                    <span className="text-white/25">·</span>
                    {/* Date */}
                    <div className="flex items-center gap-1.5">
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#5fbdb3"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="3" y="4" width="18" height="18" rx="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                      <span className="text-[12px] text-white/70">
                        {formatDateLong(blog.published_at)}
                      </span>
                    </div>
                    <span className="text-white/25">·</span>
                    {/* Read time */}
                    <div className="flex items-center gap-1.5">
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#5fbdb3"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                      <span className="text-[12px] text-white/70">
                        {readTime} min read
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Article body */}
              <div className="bg-white rounded-2xl border border-[#e2eaea] shadow-[0_2px_12px_rgba(22,44,42,0.06)] p-7 sm:p-10">
                {/* Subtitle */}
                {blog.subtitle && (
                  <p className="text-[15px] sm:text-[17px] text-[#607d7b] leading-relaxed mb-8 font-medium border-l-4 border-[#5fbdb3] pl-4">
                    {blog.subtitle}
                  </p>
                )}

                {/* Second image */}
                {secondImage && (
                  <div className="my-8 rounded-xl overflow-hidden shadow-[0_4px_20px_rgba(22,44,42,0.10)] border border-[#e2eaea]">
                    <img
                      src={secondImage}
                      alt={`${blog.title} — image 2`}
                      className="w-full h-auto max-h-[380px] object-cover block"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/default.svg";
                      }}
                    />
                  </div>
                )}

                {/* Body content */}
                {blog.content && (
                  <div
                    className="prose prose-lg max-w-none text-[#374747] leading-[1.9] text-[15px] sm:text-[16px]
                      prose-headings:text-[#1e4a46] prose-headings:font-bold
                      prose-a:text-[#ff601e] prose-a:no-underline hover:prose-a:underline
                      prose-strong:text-[#1e4a46]
                      prose-img:rounded-2xl prose-img:shadow-md"
                    dangerouslySetInnerHTML={{ __html: blog.content }}
                  />
                )}

                {/* Quote */}
                {blog.quote && (
                  <div
                    className="relative my-12 rounded-2xl overflow-hidden p-8 sm:p-12 flex items-center justify-center bg-cover bg-center"
                    style={{ backgroundImage: `url(${bg3})` }}
                  >
                    <div className="absolute inset-0 bg-[#162c2a]/75" />
                    <img
                      src={dots}
                      className="absolute top-0 left-0 w-24 sm:w-32 opacity-60 pointer-events-none"
                      alt=""
                    />
                    <img
                      src={yellow}
                      className="absolute bottom-0 left-0 w-16 sm:w-20 opacity-80 pointer-events-none z-10"
                      alt=""
                    />
                    <blockquote className="relative z-10 italic text-[15px] sm:text-[20px] lg:text-[24px] font-semibold text-white text-center leading-relaxed max-w-[700px]">
                      "{blog.quote}"
                    </blockquote>
                  </div>
                )}

                {/* Back button */}
                <div className="mt-10 pt-6 border-t border-[#e2eaea]">
                  <Link
                    to="/blogs"
                    className="inline-flex items-center gap-2 bg-[#162c2a] hover:bg-[#ff601e] text-white font-semibold text-[13px] px-6 h-10 rounded-xl transition-all duration-200 shadow-[0_2px_10px_rgba(22,44,42,0.25)] hover:-translate-y-0.5"
                  >
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M19 12H5M12 5l-7 7 7 7" />
                    </svg>
                    Back to Blog
                  </Link>
                </div>
              </div>
            </div>

            {/* ── RIGHT: 25% — Sidebar ── */}
            <div className="w-full lg:w-[25%] flex-shrink-0">
              <div className="sticky top-6 flex flex-col gap-5">
                {/* Recent Blogs */}
                <div className="bg-white rounded-2xl border border-[#e2eaea] shadow-[0_2px_12px_rgba(22,44,42,0.06)] overflow-hidden">
                  {/* Sidebar header */}
                  <div className="px-5 py-4 border-b border-[#e2eaea] flex items-center gap-3 bg-[#f8fafa]">
                    <span className="w-3 h-3 rounded-full bg-[#ff601e] flex-shrink-0" />
                    <h3 className="font-bold text-[13px] text-[#1e4a46] uppercase tracking-wider">
                      Recent Articles
                    </h3>
                  </div>

                  <div className="divide-y divide-[#f1f5f5]">
                    {allBlogs.length === 0 ? (
                      <p className="text-[12px] text-[#8faba9] text-center py-8">
                        No recent articles
                      </p>
                    ) : (
                      allBlogs.map((rb, i) => (
                        <Link
                          key={rb.id}
                          to={`/blogs/${rb.slug}`}
                          className="group flex gap-3 p-4 hover:bg-[#f8fafa] transition-colors duration-200"
                          style={{
                            animation: `fadeInUp 0.3s ease ${i * 60}ms both`,
                          }}
                        >
                          {/* Thumbnail */}
                          <div className="w-[68px] h-[68px] rounded-xl overflow-hidden flex-shrink-0 border border-[#e2eaea]">
                            <img
                              src={rb.blog_images?.[0]?.image_url}
                              alt={rb.title}
                              className="w-full h-full object-cover object-center block group-hover:scale-105 transition-transform duration-300"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "/default.svg";
                              }}
                            />
                          </div>

                          {/* Text */}
                          <div className="flex flex-col justify-center gap-1 min-w-0">
                            {rb.blog_type?.name && (
                              <span className="text-[9px] font-bold text-[#ff601e] uppercase tracking-wide">
                                {rb.blog_type.name}
                              </span>
                            )}
                            <p className="text-[12px] font-semibold text-[#1e4a46] leading-snug line-clamp-2 group-hover:text-[#ff601e] transition-colors duration-200">
                              {rb.title}
                            </p>
                            <span className="text-[10px] text-[#8faba9]">
                              {formatDate(rb.published_at)}
                            </span>
                          </div>
                        </Link>
                      ))
                    )}
                  </div>
                </div>

                {/* Category Tag Cloud */}
                {blog.blog_type?.name && (
                  <div className="bg-white rounded-2xl border border-[#e2eaea] shadow-[0_2px_12px_rgba(22,44,42,0.06)] p-5">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="w-3 h-3 rounded-full bg-[#5fbdb3] flex-shrink-0" />
                      <h3 className="font-bold text-[13px] text-[#1e4a46] uppercase tracking-wider">
                        Category
                      </h3>
                    </div>
                    <Link
                      // to="/blogs"
                      className="inline-flex items-center gap-2 bg-[#eaf4f3] hover:bg-[#1e4a46] text-[#1e4a46] hover:text-white text-[12px] font-semibold px-4 py-2 rounded-xl transition-all duration-200 border border-[#d4ecea] hover:border-[#1e4a46]"
                    >
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                        <line x1="7" y1="7" x2="7.01" y2="7" />
                      </svg>
                      {blog.blog_type.name}
                    </Link>
                  </div>
                )}

                {/* Share Box */}
                <div className="bg-white rounded-2xl border border-[#e2eaea] shadow-[0_2px_12px_rgba(22,44,42,0.06)] p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="w-3 h-3 rounded-full bg-[#1e4a46] flex-shrink-0" />
                    <h3 className="font-bold text-[13px] text-[#1e4a46] uppercase tracking-wider">
                      Share Article
                    </h3>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Copy link */}
                    <button
                      onClick={() =>
                        navigator.clipboard?.writeText(window.location.href)
                      }
                      className="flex items-center gap-1.5 bg-[#f8fafa] hover:bg-[#1e4a46] text-[#374747] hover:text-white text-[11px] font-semibold px-3 py-2 rounded-lg border border-[#e2eaea] hover:border-[#1e4a46] transition-all duration-200"
                    >
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                      </svg>
                      Copy Link
                    </button>
                    {/* WhatsApp */}
                    {/* <a
                      href={`https://wa.me/?text=${encodeURIComponent(blog.title + " " + window.location.href)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 bg-[#25d366]/10 hover:bg-[#25d366] text-[#25d366] hover:text-white text-[11px] font-semibold px-3 py-2 rounded-lg border border-[#25d366]/30 hover:border-[#25d366] transition-all duration-200"
                    >
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
                      </svg>
                      WhatsApp
                    </a> */}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
};

export default BlogDetails;
